import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink,
  FileText,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createInsurancePolicy,
  createRentalLicense,
  listInsurancePolicies,
  listProperties,
  listRentalLicenses,
  removeInsurancePolicy,
  removeRentalLicense,
  seedInsurancePolicies,
  updateInsurancePolicy,
  updateRentalLicense,
} from "@/services/dataService";
import { formatCurrency, formatDate, toDateInputValue } from "@/lib/format";

const DAY_MS = 24 * 60 * 60 * 1000;

function dateToTs(value) {
  if (!value) return undefined;
  const ts = new Date(`${value}T12:00:00`).getTime();
  return Number.isNaN(ts) ? undefined : ts;
}

function renewalBadge(endTs) {
  if (!endTs) return null;
  const days = Math.floor((endTs - Date.now()) / DAY_MS);
  if (days < 0)
    return { label: `Expired ${Math.abs(days)}d ago`, variant: "destructive" };
  if (days <= 30)
    return { label: `Renews in ${days}d`, variant: "destructive" };
  if (days <= 90) return { label: `Renews in ${days}d`, variant: "secondary" };
  return { label: `Renews ${formatDate(endTs)}`, variant: "outline" };
}

const EMPTY_INSURANCE = {
  propertyId: "",
  propertyLabel: "",
  insuranceName: "",
  policyNumber: "",
  termStart: "",
  termEnd: "",
  premium: "",
  agent: "",
  notes: "",
};

const EMPTY_LICENSE = {
  propertyId: "",
  propertyLabel: "",
  category: "",
  licenseNumber: "",
  dateFrom: "",
  dateTo: "",
  unitFees: "",
  link: "",
  notes: "",
};

export default function Insurance() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [tab, setTab] = useState("insurance");
  const [insOpen, setInsOpen] = useState(false);
  const [licOpen, setLicOpen] = useState(false);
  const [insEditing, setInsEditing] = useState(null);
  const [licEditing, setLicEditing] = useState(null);
  const [insForm, setInsForm] = useState(EMPTY_INSURANCE);
  const [licForm, setLicForm] = useState(EMPTY_LICENSE);

  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId),
  });
  const insuranceQuery = useQuery({
    queryKey: ["insurancePolicies", userId],
    queryFn: () => listInsurancePolicies(),
    enabled: Boolean(userId),
  });
  const licensesQuery = useQuery({
    queryKey: ["rentalLicenses", userId],
    queryFn: () => listRentalLicenses(),
    enabled: Boolean(userId),
  });

  const properties = propertiesQuery.data || [];
  const labelForProperty = (id) => {
    const property = properties.find((p) => String(p._id) === String(id));
    if (!property) return "";
    return `${property.address}, ${property.city}, ${property.state} ${property.zipCode}`.trim();
  };

  const insSorted = useMemo(
    () =>
      [...(insuranceQuery.data || [])].sort(
        (a, b) => (a.termEnd || 0) - (b.termEnd || 0),
      ),
    [insuranceQuery.data],
  );
  const licSorted = useMemo(
    () =>
      [...(licensesQuery.data || [])].sort(
        (a, b) => (a.dateTo || 0) - (b.dateTo || 0),
      ),
    [licensesQuery.data],
  );

  const insSave = useMutation({
    mutationFn: (payload) =>
      insEditing
        ? updateInsurancePolicy(payload)
        : createInsurancePolicy(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["insurancePolicies", userId],
      });
      setInsOpen(false);
      setInsEditing(null);
      setInsForm(EMPTY_INSURANCE);
      toast({ title: "Insurance saved" });
    },
    onError: (error) =>
      toast({
        title: "Could not save",
        description: error.message,
        variant: "destructive",
      }),
  });
  const insDelete = useMutation({
    mutationFn: removeInsurancePolicy,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["insurancePolicies", userId],
      }),
    onError: (error) =>
      toast({
        title: "Could not delete",
        description: error.message,
        variant: "destructive",
      }),
  });
  const importPolicies = useMutation({
    mutationFn: seedInsurancePolicies,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: ["insurancePolicies", userId],
      });
      toast({
        title: "Insurance policies updated",
        description: `${result.created} added · ${result.updated} refreshed`,
      });
    },
    onError: (error) =>
      toast({
        title: "Could not update insurance policies",
        description: error.message,
        variant: "destructive",
      }),
  });

  const licSave = useMutation({
    mutationFn: (payload) =>
      licEditing ? updateRentalLicense(payload) : createRentalLicense(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["rentalLicenses", userId],
      });
      setLicOpen(false);
      setLicEditing(null);
      setLicForm(EMPTY_LICENSE);
      toast({ title: "License saved" });
    },
    onError: (error) =>
      toast({
        title: "Could not save",
        description: error.message,
        variant: "destructive",
      }),
  });
  const licDelete = useMutation({
    mutationFn: removeRentalLicense,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["rentalLicenses", userId] }),
    onError: (error) =>
      toast({
        title: "Could not delete",
        description: error.message,
        variant: "destructive",
      }),
  });

  function openInsCreate() {
    setInsEditing(null);
    setInsForm(EMPTY_INSURANCE);
    setInsOpen(true);
  }
  function openInsEdit(policy) {
    setInsEditing(policy);
    setInsForm({
      propertyId: policy.propertyId ? String(policy.propertyId) : "",
      propertyLabel: policy.propertyLabel || "",
      insuranceName: policy.insuranceName || "",
      policyNumber: policy.policyNumber || "",
      termStart: toDateInputValue(policy.termStart),
      termEnd: toDateInputValue(policy.termEnd),
      premium: policy.premium != null ? String(policy.premium) : "",
      agent: policy.agent || "",
      notes: policy.notes || "",
    });
    setInsOpen(true);
  }
  function openLicCreate() {
    setLicEditing(null);
    setLicForm(EMPTY_LICENSE);
    setLicOpen(true);
  }
  function openLicEdit(license) {
    setLicEditing(license);
    setLicForm({
      propertyId: license.propertyId ? String(license.propertyId) : "",
      propertyLabel: license.propertyLabel || "",
      category: license.category || "",
      licenseNumber: license.licenseNumber || "",
      dateFrom: toDateInputValue(license.dateFrom),
      dateTo: toDateInputValue(license.dateTo),
      unitFees: license.unitFees != null ? String(license.unitFees) : "",
      link: license.link || "",
      notes: license.notes || "",
    });
    setLicOpen(true);
  }

  function saveInsurance() {
    const base = {
      propertyId: insForm.propertyId || undefined,
      propertyLabel: insForm.propertyLabel.trim(),
      insuranceName: insForm.insuranceName.trim(),
      policyNumber: insForm.policyNumber.trim(),
      termStart: dateToTs(insForm.termStart),
      termEnd: dateToTs(insForm.termEnd),
      premium: Number(insForm.premium) || 0,
      agent: insForm.agent.trim() || undefined,
      notes: insForm.notes.trim() || undefined,
    };
    insSave.mutate(insEditing ? { id: insEditing._id, ...base } : base);
  }
  function saveLicense() {
    const base = {
      propertyId: licForm.propertyId || undefined,
      propertyLabel: licForm.propertyLabel.trim(),
      category: licForm.category.trim(),
      licenseNumber: licForm.licenseNumber.trim(),
      dateFrom: dateToTs(licForm.dateFrom),
      dateTo: dateToTs(licForm.dateTo),
      unitFees: Number(licForm.unitFees) || 0,
      link: licForm.link.trim() || undefined,
      notes: licForm.notes.trim() || undefined,
    };
    licSave.mutate(licEditing ? { id: licEditing._id, ...base } : base);
  }

  const insValid =
    insForm.propertyLabel.trim() &&
    insForm.insuranceName.trim() &&
    insForm.policyNumber.trim() &&
    insForm.termStart &&
    insForm.termEnd;
  const licValid =
    licForm.propertyLabel.trim() &&
    licForm.category.trim() &&
    licForm.licenseNumber.trim() &&
    licForm.dateFrom &&
    licForm.dateTo;

  return (
    <PageLayout
      title="Insurance & Licenses"
      eyebrow="Compliance"
      subtitle="Every policy and rental license in one place, sorted by what renews next so nothing lapses."
      onRefresh={() => {
        insuranceQuery.refetch();
        licensesQuery.refetch();
      }}
      isRefreshing={insuranceQuery.isFetching || licensesQuery.isFetching}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={tab === "insurance" ? "default" : "outline"}
              className="gap-2"
              onClick={() => setTab("insurance")}
            >
              <ShieldCheck className="h-4 w-4" />
              Insurance
              <span className="ml-1 rounded-full bg-background/30 px-1.5 text-xs tabular-nums">
                {insSorted.length}
              </span>
            </Button>
            <Button
              size="sm"
              variant={tab === "licenses" ? "default" : "outline"}
              className="gap-2"
              onClick={() => setTab("licenses")}
            >
              <FileText className="h-4 w-4" />
              Rental licenses
              <span className="ml-1 rounded-full bg-background/30 px-1.5 text-xs tabular-nums">
                {licSorted.length}
              </span>
            </Button>
          </div>
          {tab === "insurance" ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => importPolicies.mutate()}
                disabled={importPolicies.isPending}
              >
                {importPolicies.isPending
                  ? "Updating…"
                  : "Update my 5 policies"}
              </Button>
              <Button onClick={openInsCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                Add policy
              </Button>
            </div>
          ) : (
            <Button onClick={openLicCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              Add license
            </Button>
          )}
        </div>

        {tab === "insurance" ? (
          <div className="grid gap-3">
            {insSorted.map((policy) => {
              const badge = renewalBadge(policy.termEnd);
              return (
                <Card key={policy._id}>
                  <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold">
                          {policy.insuranceName}
                        </h3>
                        {badge ? (
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        ) : null}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {policy.propertyLabel}
                      </p>
                      <div className="grid gap-0.5 text-sm">
                        <p>Policy #{policy.policyNumber}</p>
                        <p className="text-muted-foreground">
                          {formatDate(policy.termStart)} –{" "}
                          {formatDate(policy.termEnd)} ·{" "}
                          {formatCurrency(policy.premium)}/yr
                        </p>
                        {policy.agent ? (
                          <p className="text-muted-foreground">
                            Agent: {policy.agent}
                          </p>
                        ) : null}
                        {policy.notes ? (
                          policy.notes.startsWith("http") ? (
                            <a
                              href={policy.notes}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline break-all"
                            >
                              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                              {policy.notes}
                            </a>
                          ) : (
                            <p className="text-muted-foreground">
                              {policy.notes}
                            </p>
                          )
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openInsEdit(policy)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-rose-600"
                        onClick={() =>
                          window.confirm("Delete this policy?") &&
                          insDelete.mutate(policy._id)
                        }
                        aria-label="Delete policy"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {!insuranceQuery.isLoading && insSorted.length === 0 ? (
              <EmptyState
                icon={ShieldCheck}
                label="No insurance policies yet"
                hint="Add each property's policy to track premiums and renewal dates."
                onAdd={openInsCreate}
                addLabel="Add policy"
              />
            ) : null}
          </div>
        ) : (
          <div className="grid gap-3">
            {licSorted.map((license) => {
              const badge = renewalBadge(license.dateTo);
              return (
                <Card key={license._id}>
                  <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold">
                          {license.category}
                        </h3>
                        {badge ? (
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        ) : null}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {license.propertyLabel}
                      </p>
                      <div className="grid gap-0.5 text-sm">
                        <p>License #{license.licenseNumber}</p>
                        <p className="text-muted-foreground">
                          {formatDate(license.dateFrom)} –{" "}
                          {formatDate(license.dateTo)}
                          {license.unitFees
                            ? ` · ${formatCurrency(license.unitFees)} fees`
                            : ""}
                        </p>
                        {license.notes ? (
                          <p className="text-muted-foreground">
                            {license.notes}
                          </p>
                        ) : null}
                      </div>
                      {license.link ? (
                        <a
                          href={license.link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Open licensing portal
                        </a>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openLicEdit(license)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-rose-600"
                        onClick={() =>
                          window.confirm("Delete this license?") &&
                          licDelete.mutate(license._id)
                        }
                        aria-label="Delete license"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {!licensesQuery.isLoading && licSorted.length === 0 ? (
              <EmptyState
                icon={FileText}
                label="No rental licenses yet"
                hint="Track each county rental license and its renewal date and fees."
                onAdd={openLicCreate}
                addLabel="Add license"
              />
            ) : null}
          </div>
        )}
      </div>

      {/* Insurance dialog */}
      <Dialog open={insOpen} onOpenChange={setInsOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {insEditing ? "Edit policy" : "Add insurance policy"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <PropertyPicker
              properties={properties}
              value={insForm.propertyId}
              onChange={(id) =>
                setInsForm((prev) => ({
                  ...prev,
                  propertyId: id,
                  propertyLabel: id ? labelForProperty(id) : prev.propertyLabel,
                }))
              }
            />
            <div className="space-y-1">
              <Label>Property label</Label>
              <Input
                value={insForm.propertyLabel}
                onChange={(event) =>
                  setInsForm((prev) => ({
                    ...prev,
                    propertyLabel: event.target.value,
                  }))
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Insurer</Label>
                <Input
                  value={insForm.insuranceName}
                  onChange={(event) =>
                    setInsForm((prev) => ({
                      ...prev,
                      insuranceName: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Policy number</Label>
                <Input
                  value={insForm.policyNumber}
                  onChange={(event) =>
                    setInsForm((prev) => ({
                      ...prev,
                      policyNumber: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Term start</Label>
                <Input
                  type="date"
                  value={insForm.termStart}
                  onChange={(event) =>
                    setInsForm((prev) => ({
                      ...prev,
                      termStart: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Term end</Label>
                <Input
                  type="date"
                  value={insForm.termEnd}
                  onChange={(event) =>
                    setInsForm((prev) => ({
                      ...prev,
                      termEnd: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Premium ($/yr)</Label>
                <Input
                  type="number"
                  value={insForm.premium}
                  onChange={(event) =>
                    setInsForm((prev) => ({
                      ...prev,
                      premium: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Agent (optional)</Label>
              <Input
                value={insForm.agent}
                onChange={(event) =>
                  setInsForm((prev) => ({ ...prev, agent: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Notes / billing link (optional)</Label>
              <Input
                value={insForm.notes}
                onChange={(event) =>
                  setInsForm((prev) => ({ ...prev, notes: event.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveInsurance}
              disabled={insSave.isPending || !insValid}
            >
              {insSave.isPending ? "Saving…" : "Save policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* License dialog */}
      <Dialog open={licOpen} onOpenChange={setLicOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {licEditing ? "Edit license" : "Add rental license"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <PropertyPicker
              properties={properties}
              value={licForm.propertyId}
              onChange={(id) =>
                setLicForm((prev) => ({
                  ...prev,
                  propertyId: id,
                  propertyLabel: id ? labelForProperty(id) : prev.propertyLabel,
                }))
              }
            />
            <div className="space-y-1">
              <Label>Property label</Label>
              <Input
                value={licForm.propertyLabel}
                onChange={(event) =>
                  setLicForm((prev) => ({
                    ...prev,
                    propertyLabel: event.target.value,
                  }))
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Category</Label>
                <Input
                  placeholder="Baltimore County Rent License"
                  value={licForm.category}
                  onChange={(event) =>
                    setLicForm((prev) => ({
                      ...prev,
                      category: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>License number</Label>
                <Input
                  value={licForm.licenseNumber}
                  onChange={(event) =>
                    setLicForm((prev) => ({
                      ...prev,
                      licenseNumber: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Date from</Label>
                <Input
                  type="date"
                  value={licForm.dateFrom}
                  onChange={(event) =>
                    setLicForm((prev) => ({
                      ...prev,
                      dateFrom: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Date to</Label>
                <Input
                  type="date"
                  value={licForm.dateTo}
                  onChange={(event) =>
                    setLicForm((prev) => ({
                      ...prev,
                      dateTo: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Unit fees ($)</Label>
                <Input
                  type="number"
                  value={licForm.unitFees}
                  onChange={(event) =>
                    setLicForm((prev) => ({
                      ...prev,
                      unitFees: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Portal link (optional)</Label>
              <Input
                placeholder="https://…"
                value={licForm.link}
                onChange={(event) =>
                  setLicForm((prev) => ({ ...prev, link: event.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Input
                value={licForm.notes}
                onChange={(event) =>
                  setLicForm((prev) => ({ ...prev, notes: event.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLicOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveLicense}
              disabled={licSave.isPending || !licValid}
            >
              {licSave.isPending ? "Saving…" : "Save license"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

function PropertyPicker({ properties, value, onChange }) {
  return (
    <div className="space-y-1">
      <Label>Property (optional — fills the label)</Label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
      >
        <option value="">Type the label manually below</option>
        {properties.map((property) => (
          <option key={property._id} value={property._id}>
            {property.name || property.address}
          </option>
        ))}
      </select>
    </div>
  );
}

function EmptyState({ icon: Icon, label, hint, onAdd, addLabel }) {
  return (
    <Card>
      <CardContent className="py-14 text-center">
        <Icon className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="mt-3 font-medium">{label}</p>
        <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
        <Button onClick={onAdd} className="mt-4 gap-2">
          <Plus className="h-4 w-4" />
          {addLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
